/**
 * Session API
 *
 * Typed wrappers for agent session endpoints including SSE streaming.
 * Consumes the KodeonApiClient singleton.
 *
 * Architecture: Layer 3 (Feature Module)
 * - Calls KodeonApiClient from Layer 2
 * - Types defined in features/session/types.ts
 * - Consumed by session components and store
 */

import { api } from '@/lib/api'
import type { SessionEvent } from '@/features/agents/types'

/**
 * Send a message to an agent and stream the response.
 *
 * Uses POST-based SSE streaming via api.streamPost().
 * Returns an AbortController that can be used to cancel the stream.
 *
 * Endpoint: POST /v1/agents/{agentId}/stream
 *
 * @param agentId - Agent ID to send message to
 * @param message - User message content
 * @param sessionId - Optional session ID (backend will create new if omitted)
 * @param onEvent - Callback for each streaming event
 * @param onError - Callback for stream errors
 * @param userId - Operator user ID (backend overrides with JWT identity; required by schema)
 * @returns Object with { abort: () => void, sessionId: string }
 */
export function sendMessageStream(
  agentId: string,
  message: string,
  sessionId: string | null,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onEvent: (event: any) => void,
  onError?: (error: Error) => void,
  userId?: string,
  onComplete?: () => void
): { abort: () => void; sessionId: string } {
  const controller = new AbortController()
  const effectiveSessionId = sessionId || generateSessionId()

  // Start the stream
  api.streamPost(
    `/v1/agents/${agentId}/stream`,
    {
      // user_id is required by AgentRunRequest schema. The backend auth
      // middleware overrides this with the JWT identity, but Pydantic
      // validation runs first so we must supply a non-empty value.
      user_id: userId ?? 'operator',
      session_id: effectiveSessionId,
      new_message: {
        role: 'user',
        parts: [{ type: 'text', text: message }],
      },
    },
    onEvent,
    onError,
    controller.signal
  ).then(() => {
    // Stream completed — ensure caller can finalize streaming state
    // even if no explicit final/disposition event was received.
    onComplete?.()
  }).catch((err) => {
    // streamPost handles errors via onError callback
    // This catch is for any unhandled promise rejections
    console.error('Stream error:', err)
  })

  // Return abort function and session ID
  return {
    abort: () => controller.abort(),
    sessionId: effectiveSessionId,
  }
}

/**
 * Fetch session history by session ID.
 *
 * Endpoint: GET /v1/agents/{agent_id}/sessions/{session_id}/history
 *
 * @param agentId - Agent ID that owns the session
 * @param sessionId - Session ID to fetch history for
 * @returns Array of session events (messages, tool calls, etc.)
 */
export async function getSessionHistory(agentId: string, sessionId: string): Promise<SessionEvent[]> {
  return api.get<SessionEvent[]>(`/v1/agents/${agentId}/sessions/${sessionId}/history`)
}

/**
 * Generate a new session ID.
 * Format: `sess_` + 21-char random (matches existing pattern).
 */
function generateSessionId(): string {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789'
  const random = Array.from({ length: 21 }, () =>
    chars.charAt(Math.floor(Math.random() * chars.length))
  ).join('')
  return `sess_${random}`
}
